import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import type { CharacterState, HomeSnapshot } from "@/shared/contracts";
import { serverNow } from "@/shared/time/clock";
import { Icon, avatarChain, avatarStepKey, useFallbackChain, type AvatarStep } from "@/shared/ui";
import { backAtPhrase } from "@/features/pets/backAt";
import { PortraitStepView } from "@/features/pets/PetPortrait";
import { sceneAsset } from "./PetFigure";

/** 到家卡片头像的一级：TA 的星球形象（figure），或全站头像退回链里的一级（照片 / 演示小灰猫 / 物种插画 / 爪印）。 */
type ArrivalStep = { kind: "figure"; src: string } | AvatarStep;

const arrivalStepKey = (step: ArrivalStep) => (step.kind === "figure" ? `figure:${step.src}` : avatarStepKey(step));

/**
 * 卡片里固定写死的几段按意思断行（2026-09-25 巡检：390 深色“给了第一只住进来的伙 / 伴”、320 宽欢迎语“你。”单独掉行）。
 * 280–430 九个宽度逐条试排过：正常断行加 pretty 仍会拆词（“到家”“慢慢”“伙伴”“收进”），所以这几段配 home.css 的 keep-all + pretty，
 * 只在标点、<wbr /> 和空格处断；“TA”和后面的字之间用不断行空格（keep-all 下那个空格会成为常用断点，断成“TA / 可以去打工”）。
 * 每个断点之间的一截都比最窄的一行短，keep-all 不会被迫硬断出行首标点（tests/claude-6c2b-arrival-wrap.test.tsx 钉住）。
 */
const NBSP = String.fromCharCode(160);

/**
 * 这只是不是家里后住进来的：入住欢迎星币每个家只发一次，给第一只住进来的伙伴（后端 web_home/service.py 的 ensure_welcome；
 * I 2026-09-24 拍板维持，由前端说明）。到家时刻只在 TA 刚住进来的那一次出现，所以家里已经有别的伙伴“已住进来”，
 * 就说明 TA 不是第一只。按家园快照里这个家的宠物判断（pets；没有这一栏时看 household.pets），两份都没有就不说，不猜。
 */
export function isLaterArrival(snapshot: HomeSnapshot): boolean {
  const pets = snapshot.pets ?? snapshot.household?.pets ?? [];
  return pets.some((other) => other.pet_id !== snapshot.pet.pet_id && other.join_step === "moved_in");
}

/**
 * 这一趟大约几点到家（入住时 TA 还在外面）：家园快照里这只宠物的 back_at（HouseholdPetBrief，契约 2026-09-25），
 * 说法只用共用的 backAtPhrase(…, "home")（“大约 19:23 到家”）；没有这一栏、说不准、已过点都是 null，就不写时间，不猜。
 */
export function homeComingPhrase(snapshot: HomeSnapshot, now: Date): string | null {
  const pets = snapshot.pets ?? snapshot.household?.pets ?? [];
  const brief = pets.find((other) => other.pet_id === snapshot.pet.pet_id);
  return backAtPhrase(brief?.back_at, now, "home");
}

/**
 * 第一次入住后的“到家”时刻。只用家园快照里的真实事实：名字、TA 的样子（星球形象或 PetPortrait）、住处、
 * 由确认过的入住叮嘱投影出的欢迎语与细节；没有欢迎语时不替 TA 编一句话。
 * 只随入住成功那一次跳转出现（路由状态），关闭后不再重播。
 * 不是家里第一只住进来的，多一句入住欢迎星币的说明（第二只起钱包是 0，是有意的，不是出错）。
 * 入住时 TA 还在外面（用户 2026-09-25 拍板：领养了正在外面的居民也能先入住，这一趟回来直接回新家）：
 * 家园快照的 presence 不是 at_home 时换成“家安好了。{名字} 这一趟回来就直接回家，大约 19:23 到家。”，
 * 不说“到家了”，也不放欢迎语（“我到家啦”）——到家问候等 TA 真到家时由后端再发。
 * 住处（UI2-HOME-SMALL-01）：主人没选过城市（place.chosen=false）时，快照里的 display 是默认片区，不能写成「新家 · 某城」暗示 TA 住在那里，
 * 只写「新家」（和入住页「{名字} 会住进同一个家」一个意思）；城市定了、地址还在落定（address_pending）时补一句，和出门入口、小窝同一个说法。
 */
export function ArrivalMoment({ snapshot, character = null, onClose }: { snapshot: HomeSnapshot; character?: CharacterState | null; onClose: () => void }) {
  const primary = useRef<HTMLButtonElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const previous = document.activeElement;
    primary.current?.focus({ preventScroll: true });
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeRef.current();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      if (previous instanceof HTMLElement && previous.isConnected) previous.focus({ preventScroll: true });
    };
  }, []);
  const { pet, welcome, place } = snapshot;
  const atHome = snapshot.presence === "at_home";
  const coming = atHome ? null : homeComingPhrase(snapshot, new Date(serverNow()));
  const details = welcome?.details.slice(0, 3) ?? [];
  const figure = sceneAsset(character);
  // 头像永远是 TA 自己的样子（不写名字首字）：有星球形象用形象，否则是 PetPortrait 的画法（照片 / 演示头像 / 领养来的原创居民用物种插画 / 爪印占位）。
  // 一条退回链走到底（2026-09-27 D4 第三批）：星球形象 → 照片 → 演示小灰猫（只在演示模式）→ 插画或爪印，哪一张打不开都往下退，框的样子跟着此刻这一级。
  // 卡片和头像框昼夜都是暖纸：头像按纸面写（surface="paper"），深色下底块不再是压在纸上的深色块。
  const chain: ArrivalStep[] = [...(figure ? [{ kind: "figure" as const, src: figure.url }] : []), ...avatarChain(pet.photo_url, pet.origin, pet.species)];
  const { step, at, next } = useFallbackChain(chain, arrivalStepKey);
  const photoFailed = chain.slice(0, at).some((passed) => passed.kind === "photo");
  const pic = step.kind === "photo" || step.kind === "demo";
  return createPortal(
    <div className="ps-arrival" onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section className="ps-arrival__card" role="dialog" aria-modal="true" aria-labelledby="ps-arrival-title" data-testid="home-arrival">
        <span className={`ps-arrival__portrait${step.kind === "figure" ? " is-character" : pic ? " has-photo" : " is-placeholder"}`} aria-hidden="true">
          {step.kind === "figure" ? <img key={step.src} src={step.src} alt="" onError={next} /> : <PortraitStepView step={step} name={pet.name} photoFailed={photoFailed} size={86} surface="paper" onError={next} />}
        </span>
        {/* 滚动放在内层：卡片自己不裁切，探出卡片上沿的头像才完整（以前卡片滚动会把头像切掉一半）。 */}
        <div className="ps-arrival__body">
        <span className="ps-arrival__kicker">{place?.chosen ? `新家 · ${place.display}` : "新家"}</span>
        <h2 id="ps-arrival-title">{atHome ? `${pet.name} 到家了` : "家安好了"}</h2>
        {!atHome ? (
          <p className="ps-arrival__lead" data-testid="arrival-coming">
            {/* “大约明天 00:31 到家”整段不断行（320 宽下曾拆成“明 / 天”）；句号跟在后面，不会单独起行 */}
            {pet.name} 这一趟回来就直接回家{coming ? <>，<span className="ps-arrival__when" style={{ whiteSpace: "nowrap" }}>{coming}</span></> : null}。
          </p>
        ) : welcome ? (
          // 出处只标在有出处的地方（2026-09-24 巡检 P2）：欢迎语没有来源字段（后端按模板写，只有称呼来自叮嘱），不给它标出处；
          // 下面几项细节各自带着叮嘱编号，才在它们上面写“来自你确认过的生活叮嘱”，并用虚线和欢迎语隔开。
          <div className="ps-arrival__greeting">
            <blockquote>“{welcome.greeting}”</blockquote>
            {details.length ? (
              <div className="ps-arrival__notes">
                <p>来自你确认过的生活叮嘱</p>
                <ul aria-label="家里会记着">
                  {details.map((detail) => <li key={detail.note_id}>{detail.text}</li>)}
                </ul>
              </div>
            ) : null}
          </div>
        ) : (
          <p className="ps-arrival__lead">从现在起，<wbr />家园、手机和旅途<wbr />按真实时间<wbr />慢慢展开。</p>
        )}
        {place?.address_pending ? <p className="ps-arrival__lead" data-testid="arrival-address-pending">新家地址还在落定。落定之前，TA 先待在家里，不出门。</p> : null}
        {isLaterArrival(snapshot) ? (
          <p className="ps-arrival__lead" data-testid="arrival-welcome-coins">入住欢迎星币<wbr />每个家发一次，给了第一只住进来的伙伴。TA{NBSP}可以去打工<wbr />攒自己的星币。</p>
        ) : null}
        <ul className="ps-arrival__guide" aria-label="新家怎么住">
          <li><Icon name="sprout" size={18} /><span><strong>菜地</strong>种下的菜<wbr />按真实时间长，<wbr />成熟后<wbr />先收进仓库。</span></li>
          {/* P2（10-03）起小窝左侧的“信箱”叫“手机”（TA 的手机：来信、家庭群、和朋友的聊天都在里面） */}
          <li><Icon name="chat" size={18} /><span><strong>手机</strong>TA{NBSP}的来信和照片<wbr />会寄到这里。</span></li>
          <li><Icon name="journey" size={18} /><span><strong>旅途</strong>TA{NBSP}会按自己的节奏<wbr />出门，<wbr />你可以给建议。</span></li>
        </ul>
        <button ref={primary} type="button" className="ps-btn ps-btn--primary ps-btn--block ps-arrival__go" onClick={onClose}>
          进家看看
        </button>
        </div>
      </section>
    </div>,
    document.body,
  );
}
